// ── Export icon on every Collateral card ──
//
// One small download icon on every card in the Collateral app that holds a
// table, exporting exactly what that card shows. Mounted ONCE (App.js, while
// the Collateral app is open) rather than wired into each card, so every card
// — including ones added later — gets it without anyone remembering to.
//
// HOW. A MutationObserver watches the page; for each card ([data-card], set by
// the shared Card) that contains a <table> and does not already have an
// export button of its own, it adds the icon at the top right of the card's
// heading row (the uppercase card title) or, with no heading, in a slim strip
// at the top right.
// The icon is a plain DOM button, not React, so it never fights React over the
// card's children; it re-reads the card's tables at the moment of the click,
// so the file always matches what is on screen (filters, sorting, expanded
// rows included).
//
// WHAT IS EXPORTED. Every table in the card, header row first, one blank line
// between tables. Cells are read as displayed: plain money and numbers are
// written as numbers ($1,234.50 → 1234.50, ($1,234) and -$1,234 → -1234), a
// dash or a dot placeholder becomes empty, anything else (dates, "$1,234
// (12.3%)") is kept as text. An action column with no header and only buttons
// is left out. The download is logged like every other export.
import { useEffect } from "react";
import { downloadCSVText } from "../../shared/format";

const MARK = "data-card-export";
const ICON = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 3v12"/><path d="M7 10l5 5 5-5"/><path d="M5 21h14"/></svg>';

const cellValue = (td) => {
  const t = (td.innerText || td.textContent || "").replace(/\s+/g, " ").trim();
  if (t === "" || t === "\u2014" || t === "\u00B7" || t === "-") return "";
  const m = t.match(/^(\()?(-)?\$?\s*(-)?([\d,]+(?:\.\d+)?)(\))?$/);
  if (m && (t.includes("$") || /^[-(]?[\d,]+(\.\d+)?\)?$/.test(t)) && !/^\d{1,2}\/\d{1,2}\/\d{2,4}$/.test(t)) {
    const n = m[4].replace(/,/g, "");
    return ((m[1] && m[5]) || m[2] || m[3] ? "-" : "") + n;
  }
  return t;
};
const q = (v) => '"' + String(v).replace(/"/g, '""') + '"';

function tableToRows(table) {
  const rows = [...table.querySelectorAll("tr")].filter(tr => tr.closest("table") === table);
  const grid = rows.map(tr => [...tr.children].filter(c => /^T[HD]$/.test(c.tagName)).map(c => ({
    text: c.tagName === "TH" ? (c.innerText || c.textContent || "").replace(/\s+/g, " ").replace(/[\u25B2\u25BC\u25B4\u25BE]/g, "").trim() : cellValue(c),
    span: parseInt(c.getAttribute("colspan") || "1", 10) || 1,
    onlyButtons: !!c.querySelector("button") && !(c.innerText || "").trim(),
  })));
  // Lay the cells on a real grid: colspans widen a cell (blank fill to its
  // right), rowspans hold their column in the rows below — so a two-row header
  // ("Month over month" over "Prior | Change") lines up with the body.
  const flat = [];
  const held = [];                                   // held[col] = rows still covered by a rowspan
  rows.forEach((tr, i) => {
    const out = []; let col = 0;
    const take = () => { while (held[col] > 0) { out[col] = { text: "", onlyButtons: false }; held[col]--; col++; } };
    [...tr.children].filter(c => /^T[HD]$/.test(c.tagName)).forEach((c, k) => {
      take();
      const cell = grid[i][k];
      const rs = parseInt(c.getAttribute("rowspan") || "1", 10) || 1;
      for (let s = 0; s < cell.span; s++) {
        out[col] = s === 0 ? cell : { text: "", onlyButtons: cell.onlyButtons };
        if (rs > 1) held[col] = rs - 1;
        col++;
      }
    });
    take();
    flat.push(out);
  });
  // A header block of several rows becomes ONE header row: each column's
  // labels joined top to bottom ("Month over month 09/30/26").
  const headN = rows.findIndex(tr => ![...tr.children].every(c => c.tagName === "TH"));
  if (headN > 1) {
    const w = Math.max(...flat.slice(0, headN).map(r => r.length));
    const merged = Array.from({ length: w }, (_, j) => {
      let last = "";
      const parts = [];
      for (let r = 0; r < headN; r++) {
        const c = flat[r][j];
        // a colspan's blank fill inherits the label it sits under
        let t = c ? c.text : "";
        if (!t && r < headN - 1) { for (let k = j - 1; k >= 0; k--) { const x = flat[r][k]; if (x && x.text) { t = x.text; break; } if (!x) break; } }
        if (t && t !== last) parts.push(t);
        last = t;
      }
      return { text: parts.join(" "), onlyButtons: false };
    });
    flat.splice(0, headN, merged);
  }
  const width = Math.max(0, ...flat.map(r => r.length));
  const keep = [];
  for (let j = 0; j < width; j++) {
    const col = flat.map(r => r[j]).filter(Boolean);
    const allButtonsOrBlank = col.every(c => c.onlyButtons || c.text === "");
    if (!allButtonsOrBlank) keep.push(j);
  }
  return flat.map(r => keep.map(j => (r[j] ? r[j].text : ""))).filter(r => r.some(v => v !== ""));
}

function exportCard(card, titleText) {
  const tables = [...card.querySelectorAll("table")].filter(t => t.closest("[data-card]") === card);
  const blocks = tables.map(tableToRows).filter(b => b.length);
  if (!blocks.length) return;
  // The BOM tells Excel the file is UTF-8, so → and – survive.
  const text = "\uFEFF" + blocks.map(b => b.map(r => r.map(q).join(",")).join("\n")).join("\n\n");
  const slug = (titleText || "collateral").toLowerCase().replace(/\(.*?\)/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 60) || "collateral";
  const d = new Date();
  const stamp = d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0");
  downloadCSVText(text, `${slug}_${stamp}.csv`, { rows: blocks.reduce((a, b) => a + b.length, 0), columns: Math.max(...blocks.map(b => b[0].length)) });
}

function decorate(root) {
  root.querySelectorAll("[data-card]").forEach(card => {
    if (card.querySelector(`[${MARK}]`)) return;
    const tables = [...card.querySelectorAll("table")].filter(t => t.closest("[data-card]") === card);
    if (!tables.length) return;
    // Cards with an export of their own keep theirs only.
    if ([...card.querySelectorAll("button, a")].some(b => /export/i.test(b.textContent || ""))) return;

    // The heading: the card's uppercase title, outside any table.
    const title = [...card.querySelectorAll("div, span")].find(el =>
      el.closest("[data-card]") === card && !el.closest("table") && el.style && el.style.textTransform === "uppercase");
    const titleText = title ? (title.innerText || title.textContent || "").replace(/\s+/g, " ").trim() : "";

    const btn = document.createElement("button");
    btn.type = "button";
    btn.setAttribute(MARK, "");
    btn.title = "Export this card to CSV";
    btn.setAttribute("aria-label", "Export this card to CSV");
    btn.innerHTML = ICON;
    Object.assign(btn.style, {
      display: "inline-flex", alignItems: "center", justifyContent: "center", width: "22px", height: "22px",
      marginLeft: "8px", padding: "0", border: "0.5px solid rgba(17,101,136,0.35)", borderRadius: "5px",
      background: "transparent", color: "#116588", cursor: "pointer", verticalAlign: "middle", textTransform: "none",
      flexShrink: "0",
    });
    btn.onmouseenter = () => { btn.style.background = "#E6F7FB"; };
    btn.onmouseleave = () => { btn.style.background = "transparent"; };
    btn.onclick = (e) => { e.preventDefault(); e.stopPropagation(); exportCard(card, titleText); };

    if (title) {
      // Top right of the heading row:
      //   - a heading that is an item of a flex row (or is the flex row)
      //     gets the icon at the row's end, pushed right unless something is
      //     already pushed (two auto margins would split the space and drag
      //     that control to the middle);
      //   - a plain block heading gets it pinned to its right edge,
      //     vertically centred, without changing the heading's height.
      const isFlex = el => el && /flex/.test(getComputedStyle(el).display);
      const row = isFlex(title.parentElement) && title.parentElement !== card ? title.parentElement
                : isFlex(title) ? title : null;
      if (row) {
        const pushed = [...row.children].some(c => c !== btn && c.style && c.style.marginLeft === "auto");
        btn.style.marginLeft = pushed ? "8px" : "auto";
        row.appendChild(btn);
      } else {
        if (getComputedStyle(title).position === "static") title.style.position = "relative";
        const padR = parseFloat(getComputedStyle(title).paddingRight) || 0;
        Object.assign(btn.style, { position: "absolute", right: padR + "px", top: "50%", transform: "translateY(-50%)", marginLeft: "0" });
        title.appendChild(btn);
      }
    } else {
      // No heading: a slim right-aligned strip at the top of the card, so the
      // icon never sits on top of a column header.
      const bar = document.createElement("div");
      bar.setAttribute(MARK + "-bar", "");
      Object.assign(bar.style, { display: "flex", justifyContent: "flex-end", padding: "6px 8px 4px" });
      btn.style.marginLeft = "0";
      bar.appendChild(btn);
      card.insertBefore(bar, card.firstChild);
    }
  });
}

export const CardExports = () => {
  useEffect(() => {
    let t = null;
    const run = () => { t = null; try { decorate(document.body); } catch (e) { /* cosmetic only */ } };
    const obs = new MutationObserver(() => { if (!t) t = setTimeout(run, 150); });
    obs.observe(document.body, { childList: true, subtree: true });
    run();
    return () => { obs.disconnect(); if (t) clearTimeout(t);
      document.querySelectorAll(`[${MARK}-bar]`).forEach(b => b.remove());
      document.querySelectorAll(`[${MARK}]`).forEach(b => b.remove()); };
  }, []);
  return null;
};
